'use client';

import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import { useReducedMotion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { clamp } from 'lodash';
import { type ReactNode, useRef } from 'react';

import { usePageContext } from '@commons/contexts/page';
import { useMediaQueries } from '@commons/contexts/responsive';

import styles from './Parallax.module.scss';

// GSAP Plugins registration.

gsap.registerPlugin(ScrollTrigger, useGSAP);

// Type definition for the ParallaxBackground component properties.

type Props = {
  children: ReactNode;
  speed?: number;
  className?: string;
};

/**
 * ParallaxBackground component.
 *
 * @see https://gsap.com/resources/React/
 * @see https://gsap.com/docs/v3/Plugins/ScrollTrigger/
 */

const ParallaxBackground = ({ children, speed = 0.5, className }: Props) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { isEditing } = usePageContext();
  const hasReducedMotion = useReducedMotion();
  const { ready, minWidth } = useMediaQueries();

  // Disable the effect if user has reduced motion, is on mobile, or on the page builder.
  // Also disable during SSR or until the component is mounted.
  const disabled = hasReducedMotion || !ready || !minWidth.md || isEditing;

  useGSAP(
    () => {
      if (disabled) return;
      if (!containerRef.current || !contentRef.current) return;

      const contentHeight = contentRef.current.offsetHeight || 1;
      const containerHeight = containerRef.current.offsetHeight || 1;
      const moveDistance = Math.round((contentHeight - containerHeight) * speed);

      gsap.to(contentRef.current, {
        y: () => -moveDistance,
        ease: 'none',
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
          invalidateOnRefresh: true,
        },
      });
    },
    {
      scope: containerRef,
      dependencies: [speed, disabled],
      revertOnUpdate: true,
    }
  );

  if (disabled) return children;

  // Compute how much the content should bleed outside the container to avoid empty spaces.
  const bleedPercent = clamp(speed * 100, 25, 60);

  const classes = clsx(styles.container, className);

  return (
    <div ref={containerRef} className={classes}>
      <div
        ref={contentRef}
        className={styles.content}
        style={{
          top: `-${bleedPercent}%`,
          bottom: `-${bleedPercent}%`,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default ParallaxBackground;
